// src/countdown/provider.tsx
import * as React from "react";
import create from "zustand";
import shallow from "zustand/shallow";

// src/utils.ts
function zeroPad(number, length) {
  if (number >= Math.pow(10, length)) {
    return number.toString();
  }
  return (Array(length).join("0") + number).slice(-length);
}
function getTimeDelta(timeRemaining) {
  const seconds = Math.abs(timeRemaining) / 1e3;
  return {
    days: Math.floor(seconds / (3600 * 24)),
    hours: Math.floor(seconds / 3600 % 24),
    minutes: Math.floor(seconds / 60 % 60),
    seconds: Math.floor(seconds % 60),
    milliseconds: Number((seconds % 1 * 1e3).toFixed())
  };
}
function getFormattedTimeDelta(timeRemaining, options) {
  const { days, hours, minutes, seconds } = getTimeDelta(timeRemaining);
  const zeroPadLength = (options == null ? void 0 : options.zeroPadLength) ?? 2;
  const formattedHours = (options == null ? void 0 : options.daysInHours) ? zeroPad(hours + days * 24, zeroPadLength) : zeroPad(hours, zeroPadLength);
  return {
    days: (options == null ? void 0 : options.daysInHours) ? "" : zeroPad(days, zeroPadLength),
    hours: formattedHours,
    minutes: zeroPad(minutes, zeroPadLength),
    seconds: zeroPad(seconds, zeroPadLength),
    isComplete: timeRemaining < 1e3
  };
}
function dateOrTimestampToTimestamp(dateOrTimestamp) {
  return typeof dateOrTimestamp === "number" ? dateOrTimestamp : dateOrTimestamp.getTime();
}

// src/countdown/calculate-countdown-state.ts
function calculateCountdownState(endDate, startDate) {
  const now = Date.now();
  const endTimestamp = dateOrTimestampToTimestamp(endDate);
  const startTimestamp = startDate ? dateOrTimestampToTimestamp(startDate) : null;
  const timeRemaining = Math.max(0, endTimestamp - now);
  const progress = startTimestamp ? parseFloat(
    Math.max(
      0,
      Math.min((now - startTimestamp) / (endTimestamp - startTimestamp), 1)
    ).toFixed(2)
  ) : null;
  const isComplete = timeRemaining <= 0;
  const humanTimeRemaining = getFormattedTimeDelta(timeRemaining);
  return { timeRemaining, isComplete, progress, humanTimeRemaining };
}

// src/countdown/provider.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var Context = React.createContext(void 0);
var CountdownProvider = ({
  children,
  endDate,
  startDate,
  exitDelay,
  countdownChildren
}) => {
  const [store] = React.useState(() => {
    const endTimestamp = dateOrTimestampToTimestamp(endDate);
    const startTimestamp = startDate ? dateOrTimestampToTimestamp(startDate) : null;
    if (startTimestamp && startTimestamp >= endTimestamp) {
      throw new Error(
        "startDate must be before endDate. Please check your start and end dates."
      );
    }
    return create((set) => {
      const state = calculateCountdownState(endTimestamp, startTimestamp);
      return {
        ...state,
        endTimestamp,
        startTimestamp,
        countdownState: state.humanTimeRemaining.isComplete ? "complete" : "running",
        update() {
          set(calculateCountdownState(endTimestamp, startTimestamp));
        },
        setCountdownState(countdownState) {
          set({ countdownState });
        }
      };
    });
  });
  return /* @__PURE__ */ jsx(Context.Provider, {
    value: store,
    children: /* @__PURE__ */ jsx(Renderer, {
      exitDelay,
      countdownChildren,
      children
    })
  });
};
var useCountdownStore = () => {
  const store = React.useContext(Context);
  if (!store) {
    throw new Error(
      "useCountdownStore must be used within a CountdownProvider. Did you forget to include it?"
    );
  }
  return store;
};
function Renderer({
  countdownChildren,
  exitDelay = 0,
  children
}) {
  const {
    isComplete,
    humanIsComplete,
    countdownState,
    endTimestamp,
    startTimestamp,
    update,
    setCountdownState
  } = useCountdownStore()(
    React.useCallback(
      (state) => ({
        isComplete: state.isComplete,
        humanIsComplete: state.humanTimeRemaining.isComplete,
        countdownState: state.countdownState,
        endTimestamp: state.endTimestamp,
        startTimestamp: state.startTimestamp,
        setCountdownState: state.setCountdownState,
        update: state.update
      }),
      []
    ),
    shallow
  );
  React.useEffect(() => {
    if (isComplete)
      return;
    const interval = setInterval(update, 1e3);
    return () => {
      clearInterval(interval);
    };
  }, [endTimestamp, startTimestamp, isComplete, update]);
  React.useEffect(() => {
    switch (countdownState) {
      case "running":
        if (humanIsComplete) {
          setCountdownState(exitDelay > 0 ? "exiting" : "complete");
        }
        break;
      case "exiting": {
        const timeout = setTimeout(() => {
          setCountdownState("complete");
        }, exitDelay ?? 0);
        return () => {
          clearTimeout(timeout);
        };
      }
      default:
        break;
    }
    return;
  }, [countdownState, exitDelay, humanIsComplete, setCountdownState]);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      (!countdownChildren || isComplete) && /* @__PURE__ */ jsx(Memo, {
        children
      }),
      countdownChildren && countdownState !== "complete" && /* @__PURE__ */ jsx(Memo, {
        children: countdownChildren
      })
    ]
  });
}
var Memo = React.memo(({ children }) => {
  return /* @__PURE__ */ jsx(Fragment, {
    children
  });
});
Memo.displayName = "Memo";
export {
  CountdownProvider,
  useCountdownStore,
  zeroPad
};
